import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { aiChatKeys } from '@/hooks/api/query-keys';
import type { StepUpMode } from '@/lib/types/ai-chat';

const AI_CHAT_SETTINGS_ENDPOINT = '/ai-chat/settings';

// ============ Types (match backend response shapes) ============

export interface AiConversation {
  id: string;
  title: string;
  isPinned: boolean;
  isStarred: boolean;
  messageCount: number;
  lastMessageAt: string | null;
  model: string | null;
  provider: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolCalls: unknown;
  tokenCount: number;
  createdAt: string;
}

export interface AiConversationWithMessages extends AiConversation {
  messages: AiMessage[];
}

export interface AiChatSettings {
  enabled: boolean;
  defaultProvider: string;
  defaultModel: string;
  maxTokensPerResponse: number;
  maxToolIterations: number;
  stepUpMode: StepUpMode;
  destructiveGuardMode: 'confirmation_code' | 'passkey';
  confirmationCode: string | null;
  systemPromptOverride: string | null;
}

// ============ Query Hooks ============

export function useAiConversations(search?: string) {
  return useQuery({
    queryKey: aiChatKeys.conversationList(search),
    queryFn: () => {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      return fetchApi<{ conversations: AiConversation[] }>(`/ai-chat/conversations${params}`);
    },
    select: (data) => data.conversations,
  });
}

export function useAiConversation(id: string) {
  return useQuery({
    queryKey: aiChatKeys.conversation(id),
    queryFn: () => fetchApi<AiConversationWithMessages>(`/ai-chat/conversations/${id}`),
    enabled: !!id,
  });
}

export function useAiChatSettings() {
  return useQuery({
    queryKey: aiChatKeys.settings(),
    queryFn: () => fetchApi<AiChatSettings>(AI_CHAT_SETTINGS_ENDPOINT),
  });
}

// ============ Mutation Hooks ============

export function useCreateAiConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data?: { title?: string }) =>
      fetchApi<AiConversation>('/ai-chat/conversations', {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiChatKeys.conversations() });
    },
  });
}

export function useUpdateAiConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<AiConversation, 'title' | 'isPinned' | 'isStarred'>>;
    }) =>
      fetchApi<AiConversation>(`/ai-chat/conversations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated, { id }) => {
      queryClient.setQueryData(aiChatKeys.conversation(id), (old: AiConversationWithMessages | undefined) =>
        old ? { ...old, ...updated } : old,
      );
      queryClient.invalidateQueries({ queryKey: aiChatKeys.conversations() });
    },
  });
}

export function useDeleteAiConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<void>(`/ai-chat/conversations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiChatKeys.all });
    },
  });
}

export function useUpdateAiChatSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AiChatSettings>) =>
      fetchApi<AiChatSettings>(AI_CHAT_SETTINGS_ENDPOINT, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(aiChatKeys.settings(), updated);
    },
  });
}

// ============ Step-Up Auth Hooks ============

export interface StepUpInitiateBody {
  toolName: string;
  input: Record<string, unknown>;
  conversationId: string;
}

export interface StepUpInitiateResponse {
  factors: Array<'passkey' | 'password'>;
  passkeyOptions?: unknown;
  enrollmentRequired?: boolean;
  error?: 'passkey_enrollment_required';
  /**
   * Server-issued approval for the Confirm tier, bound to this exact tool and
   * input. The executor requires it out-of-band because the previous gate — a
   * `confirmed: true` field inside the tool arguments — was written by the
   * MODEL, so the model could approve its own destructive action.
   */
  confirmationToken?: string;
  confirmationExpiresAt?: string;
}

export interface StepUpVerifyBody {
  factor: 'passkey' | 'password';
  payload: Record<string, unknown>;
  /**
   * Load-bearing, not audit metadata: the issued token is bound to this tool
   * and input, so one verification can no longer authorise every other
   * Verify-tier tool for five minutes. Omitting them yields an UNBOUND token
   * that the executor refuses.
   */
  toolName: string;
  input: Record<string, unknown>;
}

export interface StepUpVerifyResponse {
  stepUpToken: string;
  expiresAt: string;
}

export function useStepUpInitiate() {
  return useMutation<StepUpInitiateResponse, Error, StepUpInitiateBody>({
    mutationFn: (body) =>
      fetchApi<StepUpInitiateResponse>('/ai-chat/step-up/initiate', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useStepUpVerify() {
  return useMutation<StepUpVerifyResponse, Error, StepUpVerifyBody>({
    mutationFn: (body) =>
      fetchApi<StepUpVerifyResponse>('/ai-chat/step-up/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
